import { nanoid } from '@reduxjs/toolkit';
import type { BasketListItem } from 'store/slices/basketSlice';
import type { CalculationState } from 'store/slices/calculationSlice';
import type { CalculatorFormState } from 'store/slices/calculatorFormSlice';
import { VERSION } from './EnvironmentService';

export interface BasketCalculationResult {
	calculationState: CalculationState;
	calculatorForm: CalculatorFormState;
	selectedPackage: string;
}

export interface BasketJourney {
	sessionId: string;
	items: BasketListItem[];
	activeBasketItemId: string | null;
	results: Record<string, BasketCalculationResult>;
	returnUrl: string;
}

export const BASKET_JOURNEY_KEY = `redux-basket-journey-${VERSION}`;

const resolveCalculatorUrl = (value: string): URL => {
	const sourceUrl = new URL(value, location.href);
	if (sourceUrl.protocol !== 'https:' && sourceUrl.protocol !== 'http:') {
		throw new Error('Basket calculator URLs must use HTTP or HTTPS.');
	}
	const url = new URL(location.origin);
	url.pathname = sourceUrl.pathname;
	url.search = sourceUrl.search;
	url.hash = sourceUrl.hash;
	return url;
};

export const getNextBasketItem = (journey: BasketJourney): BasketListItem | undefined =>
	journey.items.find((item) => !journey.results[item.basketItemId]);

export const loadBasketJourney = (): BasketJourney | undefined => {
	try {
		const serialized = sessionStorage.getItem(BASKET_JOURNEY_KEY);
		if (!serialized) return undefined;
		const journey: BasketJourney = JSON.parse(serialized);
		return journey?.sessionId && Array.isArray(journey.items) && journey.results && journey.returnUrl
			? journey
			: undefined;
	} catch {
		return undefined;
	}
};

export const getBasketItemUrl = (item: BasketListItem): string => {
	const url = resolveCalculatorUrl(item.url);
	url.searchParams.set('basketItemId', item.basketItemId);
	return url.href;
};

export const getActiveBasketItem = (journey = loadBasketJourney()): BasketListItem | undefined => {
	const item = journey?.items.find((item) => item.basketItemId === journey.activeBasketItemId);
	if (!item) return undefined;
	const url = resolveCalculatorUrl(item.url);
	return url.origin === location.origin &&
		url.pathname === location.pathname &&
		new URLSearchParams(location.search).get('basketItemId') === item.basketItemId
		? item
		: undefined;
};

export const startBasketJourney = (items: BasketListItem[], returnUrl: string): BasketJourney => {
	if (!items.length || items.some((item) => !item.url)) {
		throw new Error('Select products with calculator URLs before continuing.');
	}
	for (const item of items) {
		resolveCalculatorUrl(item.url);
	}

	const previousJourney = loadBasketJourney();
	const results: BasketJourney['results'] = {};
	for (const item of items) {
		const previousItem = previousJourney?.items.find(
			(entry) => entry.basketItemId === item.basketItemId && entry.id === item.id && entry.url === item.url
		);
		const previousResult = previousItem ? previousJourney.results[item.basketItemId] : undefined;
		if (previousResult) results[item.basketItemId] = previousResult;
	}

	const journey: BasketJourney = {
		sessionId: previousJourney?.sessionId ?? nanoid(),
		items,
		activeBasketItemId: null,
		results,
		returnUrl,
	};
	journey.activeBasketItemId = getNextBasketItem(journey)?.basketItemId ?? null;
	sessionStorage.setItem(BASKET_JOURNEY_KEY, JSON.stringify(journey));
	return journey;
};

export const completeBasketItem = (result: BasketCalculationResult): string | undefined => {
	const journey = loadBasketJourney();
	const activeItem = getActiveBasketItem(journey);
	if (!activeItem) {
		return undefined;
	}

	journey.results[activeItem.basketItemId] = result;
	const next = getNextBasketItem(journey);
	journey.activeBasketItemId = next?.basketItemId ?? null;
	sessionStorage.setItem(BASKET_JOURNEY_KEY, JSON.stringify(journey));
	return next ? getBasketItemUrl(next) : journey.returnUrl;
};
